var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { floor } from 'lodash';
import { Platform } from 'react-native';
import { Image as ImageCompressor } from 'react-native-compressor';
import Logger from '@/logging/Logger';
// 전송 1회의 전체 상한. xhr.timeout 은 Android(OkHttp callTimeout)만 전체 시간이고 iOS(NSURLRequest)는
// 무응답 시간이라, 조금씩 흐르는 전송이 수 분씩 이어졌다(최근 90일 30초 초과: iOS 111건 / Android 9건).
const UPLOAD_TIMEOUT_MS = 30000;
const UPLOAD_RETRY_DELAYS_MS = [1000];
// 업로드 중 매초 진행 상황을 남긴다 — TCP 연결이 끊긴 채 timeout 만 기다리는 hang 이 실제로 있는지 확인용.
const HEARTBEAT_INTERVAL_MS = 1000;
// 압축·로컬 파일 읽기는 멈추면 영원히 돌아오지 않는다 — '압축 중' 에서 5분씩 멈춘다는 실사용 보고.
const PRESIGNED_URL_TIMEOUT_MS = 15000;
// 성공한 압축은 최근 90일 2.6만 장 중 최대 2.4초(p99.9 0.7초) — 그보다 오래면 느린 게 아니라 멈춘 것.
const COMPRESS_TIMEOUT_MS = 5000;
const READ_FILE_TIMEOUT_MS = 15000;
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
/** 단계가 timeout 안에 안 끝남. 어느 단계에서 멈추는지 Crashlytics 로 추적한다. */
class UploadStallError extends Error {
    constructor(step, timeoutMs) {
        super(`Image upload stalled at ${step} for ${timeoutMs / 1000}s`);
        this.name = 'UploadStallError';
    }
}
export class UploadCancelledError extends Error {
    constructor() {
        super('Image upload cancelled by user');
        this.name = 'UploadCancelledError';
    }
}
/**
 * 작업을 timeout·사용자 취소와 경주시킨다. 지면 run 에 넘긴 signal 을 abort 해 원 작업도 멈춘다.
 * ponytail: signal 을 못 받는 작업(네이티브 압축 — 라이브러리에 취소 API 가 없다)은 멈추지 못하고 결과만 버린다.
 */
function guard(run, step, timeoutMs, signal) {
    const controller = new AbortController();
    let timer;
    let onAbort;
    return new Promise((resolve, reject) => {
        const fail = (error) => {
            reject(error);
            controller.abort();
        };
        if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
            fail(new UploadCancelledError());
            return;
        }
        onAbort = () => fail(new UploadCancelledError());
        signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', onAbort);
        timer = setTimeout(() => fail(new UploadStallError(step, timeoutMs)), timeoutMs);
        run(controller.signal).then(resolve, reject);
    }).finally(() => {
        // 원 작업이 끝내 settle 하지 않아도(멈춘 압축) 정리되게 바깥 promise 기준으로 푼다.
        clearTimeout(timer);
        if (onAbort) {
            signal === null || signal === void 0 ? void 0 : signal.removeEventListener('abort', onAbort);
        }
    });
}
/** Crashlytics 는 message 로만 구분되므로 멈춘 단계·이미지 정보를 message 에 싣는다. */
function withUploadContext(error, context) {
    const e = error instanceof Error ? error : new Error(String(error));
    e.message = `${e.message} [${context} os=${Platform.OS}]`;
    return e;
}
function describeImage(image) {
    var _a;
    const ext = (_a = image.uri.split('?')[0].split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    return `${image.width}x${image.height} ext=${ext}`;
}
function classifyError(error) {
    if (error instanceof UploadCancelledError) {
        return { errorType: 'cancelled' };
    }
    if (error instanceof UploadStallError) {
        return { errorType: 'timeout' };
    }
    if (error instanceof Error && error.name === 'AbortError') {
        return { errorType: 'timeout' };
    }
    if (error instanceof Error && error.message.includes('timed out')) {
        return { errorType: 'timeout' };
    }
    if (error instanceof UploadHttpError) {
        return { errorType: 'http_error', httpStatus: error.httpStatus };
    }
    if (error instanceof TypeError) {
        // fetch throws TypeError for network failures
        return { errorType: 'network' };
    }
    return { errorType: 'unknown' };
}
function attachDebugInfo(error, info) {
    Object.assign(error, info);
    return error;
}
function extractDebugInfo(error) {
    if (error && typeof error === 'object') {
        const e = error;
        return { imageMime: e.imageMime, imageSizeMb: e.imageSizeMb };
    }
    return {};
}
class UploadHttpError extends Error {
    constructor(message, httpStatus) {
        super(message);
        this.name = 'UploadHttpError';
        this.httpStatus = httpStatus;
    }
}
/** presigned URL에 이미지 업로드 (내부 전용) — XMLHttpRequest 사용으로 progress 지원 */
function uploadToPresignedUrl(presignedUrl, imageFileUrl, onProgress, signal, onStep) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = new URL(presignedUrl);
        onStep === null || onStep === void 0 ? void 0 : onStep('read_file');
        const imageBody = yield guard(s => 
        // RN 타입 선언의 AbortSignal 이 DOM lib 것과 달라 캐스트한다 — 런타임은 같은 폴리필 객체다.
        fetch(imageFileUrl, { signal: s }).then(resp => resp.blob()), 'read_file', READ_FILE_TIMEOUT_MS, signal);
        return new Promise((resolve, reject) => {
            if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                reject(new UploadCancelledError());
                return;
            }
            const xhr = new XMLHttpRequest();
            let timedOut = false;
            const timer = setTimeout(() => {
                timedOut = true;
                xhr.abort();
            }, UPLOAD_TIMEOUT_MS);
            const abortOnCancel = () => xhr.abort();
            xhr.onloadend = () => {
                clearTimeout(timer);
                signal === null || signal === void 0 ? void 0 : signal.removeEventListener('abort', abortOnCancel);
            };
            signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', abortOnCancel);
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress === null || onProgress === void 0 ? void 0 : onProgress(e.loaded, e.total);
                }
            };
            const debugInfo = {
                imageMime: imageBody.type,
                imageSizeMb: floor(imageBody.size / (1024 * 1024), 3),
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve({
                        url: url.protocol + '//' + url.host + url.pathname,
                        size: imageBody.size,
                    });
                }
                else {
                    reject(attachDebugInfo(new UploadHttpError(`Upload image to ${url.href} is failed. cause: ${xhr.responseText}`, xhr.status), debugInfo));
                }
            };
            xhr.onabort = () => {
                reject(timedOut
                    ? attachDebugInfo(new Error(`Image upload timed out after ${UPLOAD_TIMEOUT_MS / 1000}s`), debugInfo)
                    : new UploadCancelledError());
            };
            xhr.onerror = () => {
                reject(attachDebugInfo(new TypeError('Network request failed'), debugInfo));
            };
            xhr.open('PUT', url.href);
            xhr.setRequestHeader('content-type', imageBody.type);
            xhr.setRequestHeader('x-amz-acl', 'public-read');
            onStep === null || onStep === void 0 ? void 0 : onStep('s3_upload');
            xhr.send(imageBody);
        });
    });
}
/** 1회 재시도 포함 업로드 (사용자 취소는 재시도하지 않는다) */
function uploadToPresignedUrlWithRetry(presignedUrl, imageFileUrl, onProgress, signal, onStep) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const retryDelay of UPLOAD_RETRY_DELAYS_MS) {
            try {
                return yield uploadToPresignedUrl(presignedUrl, imageFileUrl, onProgress, signal, onStep);
            }
            catch (error) {
                if (error instanceof UploadCancelledError) {
                    throw error;
                }
                onStep === null || onStep === void 0 ? void 0 : onStep('retry_wait');
                yield delay(retryDelay);
            }
        }
        return yield uploadToPresignedUrl(presignedUrl, imageFileUrl, onProgress, signal, onStep);
    });
}
const ImageFileUtils = {
    filepath(uri) {
        const fileScheme = 'file://';
        if (uri.startsWith(fileScheme)) {
            return uri;
        }
        return fileScheme + uri;
    },
    filepathFromImageFile(imageFile) {
        return this.filepath(imageFile.uri);
    },
    /** 웹용: presigned URL 발급 + 업로드를 한 번에 처리 */
    uploadWebImage(api_1, imageUrl_1) {
        return __awaiter(this, arguments, void 0, function* (api, imageUrl, filenameExtension = 'jpeg') {
            const presignedResponse = yield api.getImageUploadUrlsPost({
                count: 1,
                filenameExtension,
            });
            const presignedUrlData = presignedResponse.data[0];
            if (!presignedUrlData) {
                throw new Error('Presigned URL 획득 실패');
            }
            const result = yield uploadToPresignedUrl(presignedUrlData.url, imageUrl);
            return result.url;
        });
    },
    /**
     * 단계마다 timeout 을 걸어 멈춘 채로 남지 않게 한다. 압축이 멈추면 원본으로 올리고,
     * 나머지 단계는 에러로 끝낸다. `signal` 로 사용자가 언제든 취소할 수 있다.
     */
    uploadImages(api_1) {
        return __awaiter(this, arguments, void 0, function* (api, images = [], purposeType, onProgress, signal) {
            const startTimeOfTotal = Date.now();
            // 실패·취소 시 "어디서 멈췄나" 보고용
            let step = 'presigned_url';
            let stepStartedAt = Date.now();
            let index = 0;
            const uploadId = `${startTimeOfTotal}-${Math.random().toString(36).slice(2, 8)}`;
            // S3 전송 진행 추적 — heartbeat 와 요약값(가장 긴 무진행 구간)의 원천
            let attempt = 0;
            let s3ImageIndex = 0;
            let s3Retries = 0;
            let bytesSent = 0;
            let bytesTotal = 0;
            let lastProgressAt = startTimeOfTotal;
            let sawProgress = false;
            let maxNoProgressMillis = 0;
            let maxNoProgressAtPct = 0;
            let maxNoProgressImageIndex = 0;
            let maxFirstProgressMillis = 0;
            const closeNoProgressGap = () => {
                if (step !== 's3_upload') {
                    return;
                }
                const gap = Date.now() - lastProgressAt;
                if (gap > maxNoProgressMillis) {
                    maxNoProgressMillis = gap;
                    // 소켓에 쓴 바이트 기준이라 100% 여도 S3 응답을 못 받은 채 멈춘 것일 수 있다
                    maxNoProgressAtPct =
                        bytesTotal > 0 ? Math.floor((100 * bytesSent) / bytesTotal) : 0;
                    maxNoProgressImageIndex = s3ImageIndex;
                }
            };
            const enterStep = (next) => {
                closeNoProgressGap();
                step = next;
                stepStartedAt = Date.now();
                if (next === 'compress') {
                    attempt = 0;
                    bytesSent = 0;
                    bytesTotal = 0;
                }
                if (next === 's3_upload') {
                    attempt += 1;
                    s3ImageIndex = index;
                    if (attempt > 1) {
                        s3Retries += 1;
                    }
                    bytesSent = 0;
                    bytesTotal = 0;
                    lastProgressAt = stepStartedAt;
                    sawProgress = false;
                }
            };
            const onS3Progress = (loaded, total) => {
                closeNoProgressGap();
                const now = Date.now();
                if (!sawProgress) {
                    sawProgress = true;
                    maxFirstProgressMillis = Math.max(maxFirstProgressMillis, now - stepStartedAt);
                }
                lastProgressAt = now;
                bytesSent = loaded;
                bytesTotal = total;
            };
            const progressSummary = () => ({
                upload_id: uploadId,
                s3_retries: s3Retries,
                max_no_progress_millis: maxNoProgressMillis,
                max_no_progress_at_pct: maxNoProgressAtPct,
                max_no_progress_image_index: maxNoProgressImageIndex,
                max_first_progress_millis: maxFirstProgressMillis,
            });
            const heartbeat = setInterval(() => {
                const now = Date.now();
                Logger.logUploadImageProgress(Object.assign({ upload_id: uploadId, step, image_index: index, image_count: images.length, attempt, bytes_sent: bytesSent, bytes_total: bytesTotal, elapsed_millis: now - startTimeOfTotal, elapsed_millis_in_step: now - stepStartedAt }, (step === 's3_upload' && {
                    millis_since_progress: now - lastProgressAt,
                })));
            }, HEARTBEAT_INTERVAL_MS);
            let durationOfUploadImages = {};
            try {
                const uploadUrls = yield guard(s => api
                    .getImageUploadUrlsPost({
                    count: images.length,
                    filenameExtension: 'jpeg',
                    purposeType,
                }, { signal: s })
                    .then(res => res.data.map(data => data.url)), 'presigned_url', PRESIGNED_URL_TIMEOUT_MS, signal);
                const durationOfGettingPresignedUrls = Date.now() - startTimeOfTotal;
                const uploadedUrls = [];
                for (index = 0; index < images.length; index++) {
                    const image = images[index];
                    const url = uploadUrls[index];
                    if (url === undefined) {
                        throw new Error('There are not enough urls.');
                    }
                    const startTimeOfImageUpload = Date.now();
                    if (image.width === 0 || image.height === 0) {
                        Logger.logError(new Error(`Image at index ${index} has zero dimension: width=${image.width}, height=${image.height}`));
                    }
                    // Compressing stage
                    onProgress === null || onProgress === void 0 ? void 0 : onProgress({
                        stage: 'compressing',
                        currentIndex: index,
                        totalImages: images.length,
                        bytesUploaded: 0,
                        totalBytes: 0,
                        imageSizeMb: 0,
                    });
                    enterStep('compress');
                    let compressedUri;
                    try {
                        compressedUri = yield guard(() => ImageCompressor.compress(image.uri, {
                            compressionMethod: 'auto',
                            maxWidth: 2560,
                            maxHeight: 2560,
                            quality: 0.7,
                            // HEIC 입력이라도 JPEG로 출력 강제. iOS 26 호환성.
                            output: 'jpg',
                        }), 'compress', COMPRESS_TIMEOUT_MS, signal);
                    }
                    catch (compressError) {
                        if (compressError instanceof UploadCancelledError) {
                            throw compressError;
                        }
                        // 압축 실패·멈춤은 원본으로 올려 사용자를 막지 않는다. 원인 추적용으로만 보고.
                        Logger.logError(withUploadContext(compressError, `image=${index + 1}/${images.length} ${describeImage(image)}`));
                        compressedUri = image.uri;
                    }
                    const durationCompress = Date.now() - stepStartedAt;
                    // Uploading stage — read_file/s3_upload 전환은 재시도까지 포함해 내부에서 알린다.
                    // 첫 progress 전에 S3 전송이 멈추면 오버레이가 '압축 중' 으로 남아 있었다 — 여기서 먼저 넘긴다.
                    onProgress === null || onProgress === void 0 ? void 0 : onProgress({
                        stage: 'uploading',
                        currentIndex: index,
                        totalImages: images.length,
                        bytesUploaded: 0,
                        totalBytes: 0,
                        imageSizeMb: 0,
                    });
                    const startUpload = Date.now();
                    const result = yield uploadToPresignedUrlWithRetry(url, compressedUri, (loaded, total) => {
                        onS3Progress(loaded, total);
                        const imageSizeMb = floor(total / (1024 * 1024), 2);
                        onProgress === null || onProgress === void 0 ? void 0 : onProgress({
                            stage: 'uploading',
                            currentIndex: index,
                            totalImages: images.length,
                            bytesUploaded: loaded,
                            totalBytes: total,
                            imageSizeMb,
                        });
                    }, signal, enterStep);
                    const durationUpload = Date.now() - startUpload;
                    durationOfUploadImages[`duration_millis_of_upload_image_${index}`] =
                        Date.now() - startTimeOfImageUpload;
                    durationOfUploadImages[`duration_millis_of_compress_${index}`] =
                        durationCompress;
                    durationOfUploadImages[`duration_millis_of_s3_upload_${index}`] =
                        durationUpload;
                    durationOfUploadImages[`size_mb_of_image_${index}`] = floor(result.size / (1024 * 1024), 2);
                    uploadedUrls.push(result.url);
                }
                closeNoProgressGap();
                yield Logger.logUploadImage(Object.assign(Object.assign(Object.assign({}, durationOfUploadImages), progressSummary()), { duration_millis_of_presigned_urls: durationOfGettingPresignedUrls, duration_millis_total: Date.now() - startTimeOfTotal, image_count: images.length }));
                return uploadedUrls;
            }
            catch (error) {
                closeNoProgressGap();
                const { errorType, httpStatus } = classifyError(error);
                const { imageMime, imageSizeMb } = extractDebugInfo(error);
                const elapsedMillisInStep = Date.now() - stepStartedAt;
                yield Logger.logUploadImageFailed({
                    errorType,
                    httpStatus,
                    imageCount: images.length,
                    retryCount: UPLOAD_RETRY_DELAYS_MS.length,
                    errorMessage: error instanceof Error ? error.message : String(error),
                    imageMime,
                    imageSizeMb,
                    step,
                    elapsedMillisInStep,
                    imageIndex: index,
                    progressSummary: progressSummary(),
                });
                const image = images[index];
                Logger.logError(withUploadContext(error, `step=${step} elapsed_in_step=${elapsedMillisInStep}ms image=${index + 1}/${images.length}` +
                    (image ? ` ${describeImage(image)}` : '')));
                throw error;
            }
            finally {
                clearInterval(heartbeat);
            }
        });
    },
};
export default ImageFileUtils;
